/**
 * Utility functions for creating and parsing unique node IDs for the TreeView.
 * Format: type-id (e.g., "folder-123", "project-456")
 */

export type NodeType = 'folder' | 'project' | 'testcase';

interface NodeKeyParts {
  type: NodeType;
  id: number;
}

const SEPARATOR = '-';

/**
 * Builds a unique node ID string.
 * Handles special case for root folder (ID 0 or null becomes 'root').
 */
export function buildNodeKey({ type, id }: { type: NodeType; id: number | null | undefined }): string {
  if (type === 'folder' && (id === 0 || id === null || id === undefined)) {
    return 'folder-root'; // Consistent root ID
  }
  if (id === null || id === undefined) {
      throw new Error(`Cannot build node key for type ${type} with null/undefined ID.`);
  }
  return `${type}${SEPARATOR}${id}`;
}

/**
 * Parses a node ID string back into its type and numeric ID.
 * Throws error for invalid formats or types.
 * Handles the special 'folder-root' case.
 */
export function parseNodeKey(nodeId: string): NodeKeyParts {
  if (nodeId === 'folder-root') {
    return { type: 'folder', id: 0 }; // Treat root as folder ID 0
  }

  const parts = nodeId.split(SEPARATOR);
  if (parts.length !== 2) {
    throw new Error(`Invalid node ID format: ${nodeId}`);
  }

  const type = parts[0] as NodeType;
  const id = parseInt(parts[1], 10);

  if (isNaN(id)) {
    throw new Error(`Invalid numeric ID in node ID: ${nodeId}`);
  }

  // Validate the type part explicitly
  if (type !== 'folder' && type !== 'project' && type !== 'testcase') {
      throw new Error(`Invalid node type in node ID: ${nodeId}`);
  }


  return { type, id };
} 